import classNames from 'classnames';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { StoreData } from 'store/root-reducer';

import { useLikeAnimation } from '../../../hooks/useLikeAnimation';
import { LikeIcon, LikeIconProps } from '../../icons/like-icon';

export interface LikeButtonProps extends LikeIconProps {
  /**
   * icon state
   */
  liked: boolean;
  classes?: string;
  onClick?: () => void;
  label?: string;
  tabIndex?: number;
  hasOverlay?: boolean;
  disabled?: boolean;
}

export const LikeButton = ({
  liked,
  label,
  classes,
  onClick,
  tabIndex,
  large,
  hasOverlay = false,
  disabled = false,
}: LikeButtonProps): JSX.Element => {
  const iconRef = useRef<HTMLSpanElement>(null);
  const { ref, isPopping, trigger } = useLikeAnimation<HTMLDivElement>(label ? iconRef : undefined);
  const isLoggedIn = useSelector(({ userData }: StoreData) => !!userData?.user?.isLoggedIn);

  const overlayBgClasses = hasOverlay ? 'like-icon-button__container--overlay' : '';

  const handleClick = () => {
    if (disabled) {
      return;
    }
    if (!liked && isLoggedIn) {
      trigger();
    }
    onClick?.();
  };

  const containerArgs = {
    ref,
    className: classNames('like-icon-button like-icon-button__container', overlayBgClasses, classes, {
      'like-icon-button__container--popping': isPopping,
    }),
    tabIndex: tabIndex || 0,
    onClick: handleClick,
  };

  if (typeof onClick === 'function') {
    containerArgs['role'] = 'button';
  }

  return (
    <div {...containerArgs}>
      <span ref={iconRef}>
        <LikeIcon liked={liked} large={large} />
      </span>
      {(label && <p>{label}</p>) || <></>}
    </div>
  );
};
